import { RouteObject } from "react-router-dom";
import { NotFound } from "src/routes/NotFound";
import { BidPackagesPageV2 } from "src/routes/bids/bid-packages-v2/BidPackagesPageV2";
import { CostAnalysisPage } from "src/routes/bids/cost-analysis/CostAnalysisPage";
import { BillPage } from "src/routes/bills/BillPage";
import { ReleaseToTradesPage } from "src/routes/developments/lot-summary/lot-release/ReleaseToTradesPage";
import { ProductOfferingConfigsComparePage } from "src/routes/developments/product-offering-configs/ProductOfferingConfigsComparePage";
import { ProductOfferingApplyToProjectsPage } from "src/routes/developments/product-offerings/ProductOfferingApplyToProjectsPage";
import { ProductOfferingPage } from "src/routes/developments/product-offerings/ProductOfferingPage";
import { ProjectProductOfferings } from "src/routes/developments/product-offerings/ProductOfferingsPage";
import { ProductOfferingVersionHistoryPage } from "src/routes/developments/product-offerings/version-history/ProductOfferingVersionHistoryPage";
import { ProjectLayout } from "src/routes/projects/ProjectLayout";
import { ProjectBeamSideNavLayout, ProjectLegacySideNavLayout } from "src/routes/projects/ProjectSideNavLayout";
import { ProjectsDevelopmentsPage } from "src/routes/projects/ProjectsDevelopmentsPage";
import { ProjectBidContractPage } from "src/routes/projects/bid-contracts/ProjectBidContractPage";
import { ProjectBidContractsPage } from "src/routes/projects/bid-contracts/ProjectBidContractsPage";
import { BudgetMetricsPage } from "src/routes/projects/budget/BudgetMetricsPage";
import { ChangeEventPage } from "src/routes/projects/change-events/ChangeEventPage";
import { ChangeEventsPage } from "src/routes/projects/change-events/ChangeEventsPage";
import { GuidedChangeEventPage } from "src/routes/projects/change-events/guided/GuidedChangeEventPage";
import { ProjectChangeLogPage } from "src/routes/projects/change-log/ProjectChangeLogPage";
import { CommitmentChangeOrderPage } from "src/routes/projects/commitments/CommitmentChangeOrderPage";
import { CommitmentPage } from "src/routes/projects/commitments/CommitmentPage";
import { CommitmentsPage } from "src/routes/projects/commitments/CommitmentsPage";
import { DashboardSwitcherPage } from "src/routes/projects/dashboard";
import { ProjectDocumentsPage } from "src/routes/projects/documents/DocumentsPage";
import { ProjectDropsPage } from "src/routes/projects/drops/ProjectDropsPage";
import { ScheduleDraftMode } from "src/routes/projects/dynamic-schedules/draft-mode/ScheduleDraftMode";
import { MilestoneDetailsPage } from "src/routes/projects/dynamic-schedules/milestone-view/MilestoneDetailsPage";
import { TaskDetailsPage } from "src/routes/projects/dynamic-schedules/task-details/TaskDetailsPage";
import { TaskMaterialsPage } from "src/routes/projects/dynamic-schedules/task-details/TaskMaterialsPage";
import { EstimatePage } from "src/routes/projects/estimates/EstimatePage";
import { ProjectEstimatesPage } from "src/routes/projects/estimates/ProjectEstimatesPage";
import { ExpensesPage } from "src/routes/projects/expenses/ExpensesPage";
import { ProjectFinishSchedulePage } from "src/routes/projects/finish-schedule/ProjectFinishSchedulePage";
import { HomeownerContractChangeOrderPage } from "src/routes/projects/homeowner-contracts/HomeownerContractChangeOrderPage";
import { HomeownerContractPage } from "src/routes/projects/homeowner-contracts/HomeownerContractPage";
import { HomeownerContractsPage } from "src/routes/projects/homeowner-contracts/HomeownerContractsPage";
import { HomeownerNotesPage } from "src/routes/projects/homeowner-notes/HomeownerNotesPage";
import { InvoicePage } from "src/routes/projects/invoices/InvoicePage";
import { ProjectInvoicesPage } from "src/routes/projects/invoices/ProjectInvoicesPage";
import { ProjectItemsPage } from "src/routes/projects/items/ProjectItemsPage";
import { JobLogsPage } from "src/routes/projects/job-logs/JobLogsPage";
import { ProjectLotSummaryPage } from "src/routes/projects/lot-summary/ProjectLotSummaryPage";
import { AppliableProductOfferingConfigsPage } from "src/routes/projects/product-offering-configs-apply/AppliableProductOfferingConfigsPage";
import { ApplyProductOfferingConfigPage } from "src/routes/projects/product-offering-configs-apply/ApplyProductOfferingConfigPage";
import { ProjectProductOfferingConfigsPage } from "src/routes/projects/product-offering-configs/ProjectProductOfferingConfigsPage";
import {
  ConstructionSpecsAndSelectionsPage,
  PreConServicesPage,
  ProjectBudgetPage,
} from "src/routes/projects/projectRoutePages";
import {
  ProjectBidPackagesRoute,
  RedirectSelectionsBaseToSpecs,
  RedirectSelectionsConstructionToSpecs,
  RedirectSelectionsPreConToServices,
  RedirectToBidContractOverview,
  RedirectToDraftSchedule,
  RedirectToProjectDashboard,
  RedirectToProjectScheduleStage,
  RedirectToSelectionDetailConstruction,
  RedirectToSelectionDetailPreCon,
} from "src/routes/projects/projectRouteRedirects";
import { ProjectSchedulePageV2 } from "src/routes/projects/schedule-v2/ProjectSchedulePageV2";
import { ProjectSettingsPage } from "src/routes/projects/settings/ProjectSettingsPage";
import { ToDosPage } from "src/routes/projects/to-dos/ToDosPage";
import { AppShell } from "src/routing/components/AppShell";
import { BeamNavbarLayout } from "src/routing/components/layouts/BeamNavbarLayout";
import { GlobalNavLayout } from "src/routing/components/layouts/GlobalNavLayout";
import { ScrollableParentLayout } from "src/routing/components/layouts/ScrollableParentLayout";
import { WorkflowPageLayout } from "src/routing/components/layouts/WorkflowPageLayout";
import * as projectPaths from "src/routing/domains/projectsPaths";

/**
 * `/projects` list page plus `/projects/:projectId/*` detail.
 *
 * Role: data router domain route.
 *
 * List stack: `AppShell` → `BeamNavbarLayout` → `ProjectsDevelopmentsPage`.
 * Detail stacks coexist during migration:
 * - Legacy: `AppShell` → `ProjectLayout` → `GlobalNavLayout` → `ProjectLegacySideNavLayout` → page.
 * - Beam: `AppShell` → `ProjectLayout` → `BeamNavbarLayout` → `ProjectBeamSideNavLayout` → page.
 * Full-page flows (finish schedule) use `ScrollableParentLayout` as a sibling of the legacy
 * SideNav; release-to-trades is also a sibling under `GlobalNavLayout`.
 * Workflow stack: `AppShell` → `ProjectLayout` → `WorkflowPageLayout` → page (no navbar).
 *
 * Path constants live in `projectsPaths.ts`.
 * Development routes live in `domains/developments/`.
 */

/**
 * "Legacy" refers to pages using the interim `BeamSideNavViewportLayout` which mimics Beam's SideNavLayout, but wraps the page content in the <ScrollableParent> wrapper.
 * This is a stopgap solution while we migrate pages to Beam's SideNavLayout, which does not use the <ScrollableParent> wrapper.
 * Migrated pages are defined in the below RouteObject `projectBeamDetailChildren`.
 */
const projectLegacyDetailChildren: RouteObject[] = [
  {
    Component: ProjectLegacySideNavLayout,
    children: [
      { index: true, Component: RedirectToProjectDashboard },
      { path: `${projectPaths.projectSettingsRoute.path}/*`, Component: ProjectSettingsPage },
      // Optional params keep the legacy `?` path strings for matching.
      { path: projectPaths.projectContractChangeOrderOptionalPath, Component: HomeownerContractChangeOrderPage },
      { path: projectPaths.projectContractRoute.path, Component: HomeownerContractPage },
      { path: projectPaths.projectContractsRoute.path, Component: HomeownerContractsPage },
      { path: projectPaths.projectCommitmentChangeOrderRoute.path, Component: CommitmentChangeOrderPage },
      { path: projectPaths.projectBillOptionalPath, Component: BillPage },
      { path: projectPaths.projectCommitmentRoute.path, Component: CommitmentPage },
      { path: `${projectPaths.projectInvoiceRoute.path}/*`, Component: InvoicePage },
      { path: projectPaths.projectEstimateRoute.path, Component: EstimatePage },
      { path: projectPaths.projectScheduleBaseRoute.path, Component: RedirectToProjectScheduleStage },
      { path: projectPaths.projectScheduleRoute.path, Component: ProjectSchedulePageV2 },
      { path: projectPaths.projectItemsRoute.path, Component: ProjectItemsPage },
      { path: `${projectPaths.projectDraftScheduleRoute.path}/*`, Component: ScheduleDraftMode },
      { path: projectPaths.projectDynamicScheduleTaskMaterialsRoute.path, Component: TaskMaterialsPage },
      { path: projectPaths.projectDynamicScheduleTaskRoute.path, Component: TaskDetailsPage },
      { path: projectPaths.projectDynamicScheduleMilestoneRoute.path, Component: MilestoneDetailsPage },
      { path: projectPaths.projectDynamicSchedulesRoute.path, Component: RedirectToDraftSchedule },
      {
        path: projectPaths.projectProductOfferingVersionHistoryRoute.path,
        Component: ProductOfferingVersionHistoryPage,
      },
      { path: projectPaths.projectProductOfferingOptionalPath, Component: ProductOfferingPage },
      {
        path: projectPaths.projectProductOfferingSpecificConfigsCompareRoute.path,
        Component: ProductOfferingConfigsComparePage,
      },
      {
        path: projectPaths.projectProductOfferingConfigsCompareRoute.path,
        Component: ProductOfferingConfigsComparePage,
      },
      {
        path: projectPaths.projectProductOfferingApplyToProjectRoute.path,
        Component: ProductOfferingApplyToProjectsPage,
      },
      {
        path: projectPaths.projectProductOfferingConfigAppliableRoute.path,
        Component: AppliableProductOfferingConfigsPage,
      },
      { path: projectPaths.projectProductOfferingConfigApplyRoute.path, Component: ApplyProductOfferingConfigPage },
      { path: projectPaths.projectBidContractOverviewRoute.path, Component: ProjectBidContractPage },
      { path: projectPaths.projectBidContractPlanPricingRoute.path, Component: ProjectBidContractPage },
      { path: projectPaths.projectBidContractUnitPricingRoute.path, Component: ProjectBidContractPage },
      { path: projectPaths.projectBidContractPurchaseOrdersRoute.path, Component: ProjectBidContractPage },
      { path: projectPaths.projectBidContractRoute.path, Component: RedirectToBidContractOverview },
      { path: projectPaths.projectSelectionsBaseRoute.path, Component: RedirectSelectionsBaseToSpecs },
      { path: projectPaths.projectSelectionsPreConRoute.path, Component: RedirectSelectionsPreConToServices },
      { path: projectPaths.projectSelectionsPreConDetailsRoute.path, Component: RedirectToSelectionDetailPreCon },
      { path: projectPaths.projectSelectionsConstructionRoute.path, Component: RedirectSelectionsConstructionToSpecs },
      {
        path: projectPaths.projectSelectionsConstructionDetailsRoute.path,
        Component: RedirectToSelectionDetailConstruction,
      },
      { path: `${projectPaths.projectPreConServicesRoute.path}/*`, Component: PreConServicesPage },
      { path: projectPaths.projectLotSummaryRoute.path, Component: ProjectLotSummaryPage },
      { path: `${projectPaths.projectSpecsAndSelectionsRoute.path}/*`, Component: ConstructionSpecsAndSelectionsPage },
      { path: `${projectPaths.projectChangeEventRoute.path}/*`, Component: ChangeEventPage },
      { path: projectPaths.projectChangeEventsRoute.path, Component: ChangeEventsPage },
      { path: `${projectPaths.projectDashboardRoute.path}/*`, Component: DashboardSwitcherPage },
      { path: projectPaths.projectExpensesRoute.path, Component: ExpensesPage },
      { path: `${projectPaths.projectToDosRoute.path}/*`, Component: ToDosPage },
      { path: `${projectPaths.projectJobLogsRoute.path}/*`, Component: JobLogsPage },
      { path: projectPaths.projectHomeownerNotesRoute.path, Component: HomeownerNotesPage },
      { path: projectPaths.projectBudgetBaseRoute.path, Component: ProjectBudgetPage },
      { path: projectPaths.projectBudgetMetricsRoute.path, Component: BudgetMetricsPage },
      { path: projectPaths.projectInvoicesRoute.path, Component: ProjectInvoicesPage },
      { path: projectPaths.projectDropsRoute.path, Component: ProjectDropsPage },
      { path: "*", Component: NotFound },
    ],
  },
  // Full-page project flows — keep `ProjectLayout` context, skip SideNav chrome.
  {
    Component: ScrollableParentLayout,
    children: [{ path: projectPaths.projectFinishScheduleRoute.path, Component: ProjectFinishSchedulePage }],
  },
  { path: `${projectPaths.projectReleaseToTradesRoute.path}/*`, Component: ReleaseToTradesPage },
  { path: "*", Component: NotFound },
];

/** Project leaves that have migrated to Beam's SideNavLayout and document-scroll page layouts. */
const projectBeamDetailChildren: RouteObject[] = [
  {
    Component: ProjectBeamSideNavLayout,
    children: [
      { path: projectPaths.projectDocumentsRoute.path, Component: ProjectDocumentsPage },
      { path: projectPaths.projectProductOfferingsRoute.path, Component: ProjectProductOfferings },
      {
        path: projectPaths.projectProductOfferingSpecificConfigsRoute.path,
        Component: ProjectProductOfferingConfigsPage,
      },
      { path: projectPaths.projectProductOfferingConfigsRoute.path, Component: ProjectProductOfferingConfigsPage },
      { path: projectPaths.projectBidPackagesRoute.path, Component: ProjectBidPackagesRoute },
      { path: projectPaths.projectBidPackagesV2Route.path, Component: BidPackagesPageV2 },
      { path: `${projectPaths.projectCostAnalysisRoute.path}/*`, Component: CostAnalysisPage },
      { path: projectPaths.projectBidContractsRoute.path, Component: ProjectBidContractsPage },
      { path: `${projectPaths.projectChangeLogRoute.path}/*`, Component: ProjectChangeLogPage },
      { path: projectPaths.projectEstimatesRoute.path, Component: ProjectEstimatesPage },
      { path: projectPaths.projectCommitmentsRoute.path, Component: CommitmentsPage },
    ],
  },
];

const projectWorkflowChildren: RouteObject[] = [
  { path: projectPaths.projectGuidedChangeEventRoute.path, Component: GuidedChangeEventPage },
];

/**
 * Project detail tree without AppShell/GlobalNav — for unit tests and stories that render
 * `ProjectLayout` + SideNav leaves. Pass `appShell: true` to `render` / `setStory` so the
 * test helper owns `path` (same slot as `AppShell` in `projectDetailMigratedRoute`).
 */
export const projectDetailRoutes: RouteObject[] = [
  {
    path: projectPaths.projectRoute.path,
    Component: ProjectLayout,
    children: [...projectBeamDetailChildren, ...projectLegacyDetailChildren],
  },
];

const projectsListRoute: RouteObject = {
  path: projectPaths.projectsRoute.path,
  Component: AppShell,
  children: [{ Component: BeamNavbarLayout, children: [{ index: true, Component: ProjectsDevelopmentsPage }] }],
};

/** Production: AppShell owns the path → pathless ProjectLayout → a chrome wrapper → leaves. */
const projectDetailMigratedRoute: RouteObject = {
  path: projectPaths.projectRoute.path,
  Component: AppShell,
  children: [
    {
      Component: ProjectLayout,
      children: [
        { Component: BeamNavbarLayout, children: projectBeamDetailChildren },
        { Component: GlobalNavLayout, children: projectLegacyDetailChildren },
        { Component: WorkflowPageLayout, children: projectWorkflowChildren },
      ],
    },
  ],
};

export const projectsRoutes: RouteObject[] = [projectsListRoute, projectDetailMigratedRoute];
